/**
 * toActivityEntry.js — RMA entry → ActivityEntryModel normalizer.
 *
 * Single source of truth for turning raw rma_case_events / rma_communications /
 * rma_inbox_emails into ActivityEntryModel descriptors consumed by ActivityEntryRow.
 *
 * Moved in from CaseTimeline.jsx:
 *   EVENT_LABELS, eventSummary (+ 17 per-type formatters), humanizeSource,
 *   die 3 vorhandenen Notiz-Regex-Matcher. (SHOPIFY_ADMIN stand hier bis
 *   2026-09-10 — mit den toten Retouren-Links entfallen.)
 * New:
 *   refund-status note matcher, shipment-added note matcher, free-text note handler.
 *
 * @param {Object} entry  Raw timeline entry (kind: 'event'|'comm'|'inbound'|'merged')
 * @param {Object} [ctx]  { onReprocessed, onUnlinked, conversation } — action
 *   callbacks plus the conversation opt-in (see CONVERSATION CONTEXT below)
 * @returns {import('../../components/common/ActivityTimeline/activityEntryModel').ActivityEntryModel}
 *
 * CONVERSATION CONTEXT (`ctx.conversation`, 2026-08-04)
 * -----------------------------------------------------
 * The V2 Conversation rail is the ONLY caller that passes it. It turns on the
 * three things a chat-style bubble needs and an audit row must not have:
 *
 *   1. `bodyHtml` / `quotedBodyHtml` — the sanitized `body_html` render. Rules
 *      in `_emailHtml()`; the sanitizer itself is `sanitizeEmailHtml.js`.
 *   2. `attachments` — the normalized `attachments_json` footer (§B).
 *   3. body text at reading size instead of the small italic quote style (§D).
 *
 * Everything else — the Audit tab, the legacy flag-OFF tree, the PO feed —
 * calls without it and gets none of the three.
 *
 * [CORRECTED 2026-08-04] This paragraph used to end "and gets byte-identical
 * output to before this change". That was wrong, and wrong in a way a reader
 * would have relied on. TWO changes in this file are deliberately GLOBAL:
 *
 *   - §E, the LLM summary is no longer displayed beside a body. One
 *     behaviour everywhere, on purpose — see the merged/inbound branches.
 *   - §C, the frontend quote-split fallback in `_bodyWithQuote` (below).
 *     Wherever `body_new` is absent and a reply marker IS present, the entry
 *     now grows a "Show quoted message" toggle — in the Audit tab and the
 *     legacy tree too. That includes HAND-WRITTEN NOTES: a note whose text
 *     happens to start a line with "Von:" or "From:" will be split. Nothing
 *     is lost (the full text is behind the toggle), but it is a visible
 *     change outside the conversation rail and must not be described as one.
 *
 * What IS unchanged outside the conversation opt-in: no HTML rendering, no
 * attachment footer, no body-size change.
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import { chip, transition } from '../../components/common/ActivityTimeline/activityEntryModel';
import { formatMoney } from '../../utils/format';
// N2b: der Zeitstrahl war die fuenfte Darstellungsregel fuer `issue_type` —
// roh, sogar ohne den `_`->Leerzeichen-Ersatz, den der Posteingang hatte.
import { issueTypeAnzeige } from '../constants';
import { formatBerlinDateTime, parseTimestamp } from '../../utils/time';
import { COMM_LABELS, STATUS_LABELS, STATUS_CONFIG } from '../constants';
import {
  CUSTOMER_RESOLUTION_OPTIONS, SERVICE_OUTCOME_OPTIONS,
  normalizeCustomerResolution, normalizeServiceOutcome,
} from '../constants';
import { DECIDER_LABELS, returnCostReasonLabel } from '../lib/returnCostProvenance';
import { splitSanitizedEmailHtml } from './sanitizeEmailHtml';
import { shopifyNumericId } from '../../utils/shopifyAdmin';
import { splitQuotedText } from './splitQuotedThread';

/**
 * Track A5-4b: minute-delta between two timestamps, on the central parser.
 *
 * Every caller feeds it a `mergedLatestDate` and a base `date` that can come
 * from DIFFERENT rows of the same column — i.e. exactly the mixed
 * offsetless / `+00:00` pairing A5-3 left behind. A bare `new Date()` would
 * report a whole browser offset of phantom spread ("×3 within 120 min") for
 * two events seconds apart. Returns 0 when either side is unusable, matching
 * the previous `NaN → 0` fallback at the one call site that had one.
 */
function _minutesBetween(later, earlier) {
  const a = parseTimestamp(later);
  const b = parseTimestamp(earlier);
  if (!a || !b) return 0;
  return Math.round((a.getTime() - b.getTime()) / 60000);
}

// ── Constants ─────────────────────────────────────────────────────────────────

// 🔴 `SHOPIFY_ADMIN` ist am 2026-09-10 hier ENTFALLEN, nicht vergessen worden.
// Seine beiden einzigen Verwender bauten `…/returns/<id>` — eine Seite, die es
// in Shopify nicht gibt (siehe die Begruendung bei Treffer 3 weiter unten).
// Mit den Links faellt die Konstante, sonst reisst der CRA-Bau an
// `no-unused-vars` (Regel 9). Wer den Link in der verschachtelten Form
// zurueckholt, holt sie mit — die Vorgabe stand auf
// `https://admin.shopify.com/store/kaffeemanuale`, env-gesteuert ueber
// `REACT_APP_SHOPIFY_ADMIN_URL`.

const API = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const SMART_COMPOSE_PREFIX = '[Smart Compose basis] ';

// Title used for a free-text note that hit no structural matcher in
// _parseNote(). Also used as the sentinel the comm branch below compares
// against to tell "structured note" from "plain note" apart — all three use
// sites (the two returns here + the comparison in toActivityEntry) MUST share
// this one constant so a future copy edit can never desync title vs. check.
const FREE_TEXT_NOTE_TITLE = 'Note';

// Event types where the full JSON payload is intentionally surfaced for debug.
const DEBUG_TYPES = new Set([
  'shopify_return_creation_failed',
]);

// Human labels for event types.
export const EVENT_LABELS = {
  shopify_return_created:         'Shopify return created',
  shopify_return_cancelled:       'Shopify return cancelled',
  shopify_return_approved:        'Shopify return approved',
  shopify_return_declined:        'Shopify return declined',
  shopify_return_linked:          'Shopify return linked',
  shopify_return_unlinked:        'Shopify return unlinked',
  status_change:                  'Status change',
  comm_inbound:                   'Inbound message',
  comm_outbound:                  'Outbound message',
  lsm_event:                      'LSM event',
  refund_event:                   'Refund event',
  mark_event:                     'Marked',
  snooze_event:                   'Snoozed (legacy feature)',
  snooze_set:                     'Follow-up set',
  snooze_cancelled:               'Follow-up cancelled',
  snooze_due:                     'Follow-up due',
  snooze_broken_by_email:         'Follow-up ended by email',
  case_reopened_by_email:         'Automatically reopened (email)',
  reopen_reverted:                'Auto-reopen reverted',
  inbox_linked_silent:            'Email linked silently (no reopen)',
  reactivation_suppressed:        'Reactivation suppressed (auto-reply)',
  customer_response_logged:       'Customer reply',
  vendor_response_logged:         'Vendor reply',
  lsm_event_received:             'LSM event received',
  lsm_milestone_booked:           'LSM booked',
  sla_approaching:                'SLA approaching',
  sla_breached:                   'SLA breached',
  refund_completed:               'Refund completed',
  refund_failed:                  'Refund failed',
  escalated_to_manual:            'Escalated to manual handling',
  photo_attachment_received:      'Photo received',
  shopify_return_creation_failed: 'Shopify return FAILED',
  comment_added:                  'Comment added',
  additional_topic_detected:      'Additional topic detected',
  topic_split_into_case:          'Split into new case',
  case_auto_created:              'Case auto-created',
  // N59 (2026-09-27): die zwei Spur-Ereignisse. Ohne Etikett rendert die Chronik
  // den Rückfall „case type changed" plus eine k:v-Zusammenfassung der Nutzlast,
  // die das Mitarbeiterkonto (`urheber`) im Klartext zeigte (Grill W5).
  case_merged_in:                 'Case merged in',
  case_type_changed:              'Case type changed',
  tracking_added:                 'Shipment added',
  tracking_removed:               'Shipment removed',
  // Neutral fallback — the attach path ("Link order", nothing was linked
  // before) and the re-point path get distinct labels via eventLabel().
  order_reassigned:               'Order linked',
  // N106 (2026-10-06): the case moved to another POSITION of the SAME order.
  // Its own type, not `order_reassigned`: that one is labelled from
  // `from.shopify_order_id` and summarised as
  // `from.shopify_order_name → to.shopify_order_name`, which for a within-order
  // move would read "SHOP-13576 → SHOP-13576" (or, with an empty left side,
  // "Order linked" — asserting a link that already existed).
  // german-words: ok: englischer Satz; "Position" ist der RMA-Fachbegriff (Route /return-positions)
  position_changed:               'Position changed',
  // E10 Stufe 1 (2026-09-25): the three case-decision traces get their own
  // label and summary. Until then they fell into the generic default branch
  // ("actor: 1 · …"), and a resolution change even lost its new value there.
  resolution_change:              'Resolution changed',
  return_cost_change:             'Return cost decision changed',
  resolution_waived:              'Resolution consequence waived',
};

/**
 * Label for one event entry. Almost always a straight EVENT_LABELS lookup;
 * `order_reassigned` is the exception because ONE event type covers two
 * different staff actions and the label must not assert a prior link that
 * never existed. Mirrors the dialog's own split
 * (CustomerOrderPicker: "Link order" vs "Change linked order").
 *
 * N47, R16 B1 (2026-10-02): `case_type_changed` is the SECOND exception of
 * exactly that shape. Since the creation trace (`backend/rma/anlage_spur.py`)
 * it covers two operations — a case being CREATED with a case type, and a case
 * type being CHANGED. `EVENT_LABELS` said "Case type changed" for both, so the
 * headline of a creation asserted a switch that never happened, directly above
 * the sentence "Case created as Return". The `bei_anlage` mark decides, the
 * same way `from.shopify_order_id` does for `order_reassigned`.
 */
export function eventLabel(entry) {
  if (entry.type === 'order_reassigned') {
    const from = (entry.payload || {}).from || {};
    return from.shopify_order_id ? 'Order re-linked' : 'Order linked';
  }
  if (entry.type === 'case_type_changed' && (entry.payload || {}).bei_anlage) {
    return 'Case created';
  }
  return EVENT_LABELS[entry.type] || String(entry.type).replace(/_/g, ' ');
}

// Human labels for tracking sources.
const SOURCE_LABELS = {
  lsm_portal:  'LSM Portal',
  lsm_email:   'LSM email',
  lsm_scraper: 'LSM Scraper',
  shopify:     'Shopify',
  manual:      'manual',
  staff:       'manual',
  system:      'System',
  inbox:       'Inbox',
  automation:  'Automation',
};

export function humanizeSource(v) {
  if (!v) return '';
  const key = String(v).toLowerCase();
  return SOURCE_LABELS[key] || String(v).replace(/_/g, ' ');
}

// N59: die Fallart als Wort. Es gibt in frontend/src/rma keine geteilte
// Beschriftung der Fallart (gemessen 2026-09-27: constants.js kennt nur
// RETURN_LANE_CASE_TYPES); zwei Zeilen hier statt einer neuen Fläche.
const CASE_TYPE_LABELS = { return: 'Return', service: 'Service', inquiry: 'Inquiry' };
function labelCaseType(v) {
  if (!v) return 'unknown';
  return CASE_TYPE_LABELS[String(v).toLowerCase()] || String(v).replace(/_/g, ' ');
}

// N59, R16 W1 (2026-09-27): der Auslöser von `case_type_changed` als Wort. Die
// Schreibermenge ist erschöpft (gemessen: vier `fallart_wechseln`-Aufrufstellen
// plus der DB-Trigger aus Migration 309): `convert_to_service`,
// `shopify_return_linked:<source>`, der Skriptpfad und `unbekannt`. Über
// `humanizeSource` würden drei davon als Halbcode oder Deutsch rendern
// („shopify return linked:webhook", „scripts/backfill rma inbox case type.py",
// „unbekannt"). Ein unbekannter Wert fällt weiter auf `humanizeSource` zurück;
// ein leerer Wert liefert '' und der Aufrufer lässt die Klammer weg.
//
// N47 (2026-10-01): eine FÜNFTE Schreiberstelle ist dazugekommen, und sie ist
// keine Wechselstelle, sondern eine Anlage: `backend/rma/anlage_spur.py`
// schreibt bei JEDER Fallanlage ein `case_type_changed` mit `bei_anlage: true`.
// Ihr Auslöser ist `lsm_autoanlage` (LSM-Kanal) bzw. `<kanal>_anlage`. Er hat
// hier ABSICHTLICH kein Etikett — die Verzweigung unten gibt ihn nie aus
// (Begründung dort), und ein Etikett ohne Aufrufer ist genau der tote Eintrag,
// nach dem die nächste Sitzung sucht (R16 W1, 2026-10-02).
const CASE_TYPE_TRIGGER_LABELS = {
  convert_to_service: 'convert to service',
  'scripts/backfill_rma_inbox_case_type.py': 'backfill script',
  unbekannt: 'unknown trigger',
};
function labelCaseTypeTrigger(v) {
  if (!v) return '';
  const key = String(v);
  if (CASE_TYPE_TRIGGER_LABELS[key]) return CASE_TYPE_TRIGGER_LABELS[key];
  if (key.startsWith('shopify_return_linked:')) return 'Shopify return linked';
  // R16 W4 (2026-10-02): der Rückfall war `humanizeSource` und damit der
  // Rohwert. Ein neuer Auslöser soll sichtbar bleiben, aber benannt — ein
  // nacktes „brandneu erfunden" in der Klammer sieht aus wie ein Etikett, das
  // jemand absichtlich so geschrieben hat.
  return `unrecognized trigger: ${humanizeSource(key)}`;
}

// N59, R16 W3: Einzahl und Mehrzahl an allen drei Zählungen gleich.
function countNoun(n, singular, plural) {
  const k = n ?? 0;
  return `${k} ${k === 1 ? singular : plural}`;
}

// Human labels for the backend SLA `type` keys (rma_sla.py SERVICE_SLAS /
// RETURN_SLAS). Shared by the timeline (sla_approaching / sla_breached events)
// and the SLA Overview page (RMASlaOverview) so the two never drift. Unknown
// keys fall back to a humanized underscore-strip at the call site.
export const SLA_KIND_LABELS = {
  case_not_acted_on:        'Case not acted on',
  vendor_no_response:       'No vendor reply',
  customer_no_response:     'No customer reply',
  action_not_taken:         'Review pending',
  customer_not_notified:    'Customer not notified',
  shipment_tracking_stale:  'No shipment tracking (5 days)',
  return_link_not_sent:     'No return link sent',
  shopify_return_missing:   'No Shopify return created',
  no_carrier_scan:          'Return not shipped',
  delivery_overdue:         'Delivery overdue',
  awaiting_inspection:      'Warehouse inspection pending',
  grade_not_actioned:       'Grade: decision pending',
  refund_not_triggered:     'Refund not triggered',
};

// ── eventSummary helpers ──────────────────────────────────────────────────────

function _fmt(v) {
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

/**
 * E10 Stufe 1: who made a case decision, as the server recorded it.
 *
 * `payload.actor` is the ORIGINATOR the backend derived from the move's
 * declared actor (`backend/rma/urheber.py::urheber_fuer`): the signed-in
 * account, a machine (`system:*`), a surface (`public:*`), or null when none
 * could be established. Null is SAID, never filled: no fallback to
 * `actor_change` (a role), to "staff", or to anything else. Rows written before
 * Stufe 1 keep what they carry (often "1"); they are a record, not rewritten.
 */
export function originatorText(actor) {
  const wer = _fmt(actor);
  return wer ? `by ${wer}` : 'no originator recorded';
}

// The event types whose summary names the originator itself (see the meta
// line in `toActivityEntry`).
const ORIGINATOR_IN_DETAIL = new Set(['resolution_change', 'return_cost_change', 'resolution_waived']);

const RESOLUTION_FIELD_LABELS = {
  customer_resolution: 'Customer resolution',
  service_outcome:     'Service outcome',
};

// The value lists of the two resolution axes, value -> label.
const RESOLUTION_VALUE_LABELS = {
  customer_resolution: Object.fromEntries(CUSTOMER_RESOLUTION_OPTIONS.map(o => [o.value, o.label])),
  service_outcome:     Object.fromEntries(SERVICE_OUTCOME_OPTIONS.map(o => [o.value, o.label])),
};

// Regel-16-Tor W9 (E10): normalize first, then label, as `RMAReports.jsx` and
// `ResolutionPanel.jsx` do. A legacy spelling reads like its canonical value;
// a value that neither the list nor the legacy map knows stays visible.
const RESOLUTION_NORMALIZERS = {
  customer_resolution: normalizeCustomerResolution,
  service_outcome:     normalizeServiceOutcome,
};

// Regel-16-Tor W4 (E10): "empty" has ONE spelling in the three decision
// summaries. Unquoted on purpose: it is not a value, it says there is none.
const NOT_SET = 'not set';

// Regel-16-Tor C1 (E10): a value stands in typographic quotes after
// "changed from … to …". Two service-outcome labels carry an arrow themselves
// ("Vendor → us → customer"), so an unquoted "A → B" could not tell from and
// to apart, neither on screen nor read aloud.
function _quoted(text) {
  return `\u201C${text}\u201D`;
}

function _changed(field, from, to) {
  return `${field} changed from ${from} to ${to}`;
}

function _resolutionValue(field, v) {
  const raw = _fmt(v);
  if (!raw) return NOT_SET;
  const canonical = (RESOLUTION_NORMALIZERS[field] || (x => x))(raw);
  return _quoted((RESOLUTION_VALUE_LABELS[field] || {})[canonical] || canonical);
}

// 🔴 NOT a reading of the money axis (Regel-15-Tor W3 zu E10). The backend has
// ONE reading (`kunde_traegt_rueckporto*`, null counts as "customer pays",
// Owner B2); the frontend exports no helper for it, and a ninth inline reading
// is exactly what `returnCostProvenance.js` forbids. So the audit names the
// STORED value of `is_voluntary_return`, all three states, and says nothing
// about who pays. Its field name is "Voluntary flag", not "Voluntary return"
// (Regel-16-Wiedervorlage, 2026-09-26): "Voluntary return" is also a value of
// the reason, and a line where both change would carry the same words twice.
function _voluntaryFlagValue(v) {
  if (v === true) return _quoted('yes');
  if (v === false) return _quoted('no');
  return NOT_SET;
}

function _reasonValue(v) {
  const raw = _fmt(v);
  return raw ? _quoted(returnCostReasonLabel(raw)) : NOT_SET;
}

// Regel-16-Tor W3 (E10): the decider reads as in the money card
// (`DECIDER_LABELS`, borrowed, not retyped). There `system_default` names NO
// decider on purpose (it is the signature of the derivation, not of a
// decision), so it reads "not set" here as well. An unknown key stays visible.
function _deciderValue(v) {
  const raw = _fmt(v);
  if (!raw || raw === 'system_default') return NOT_SET;
  return _quoted(DECIDER_LABELS[raw] || raw);
}

/**
 * Returns a human-readable one-line summary for a given event entry,
 * or null if no typed formatter exists.
 */
export function eventSummary(entry) {
  const p = entry.payload || {};
  switch (entry.type) {
    case 'tracking_added':
    case 'tracking_removed': {
      // Clean one-liner instead of raw JSON (source · carrier number · direction).
      const parts = [];
      if (p.source) parts.push(humanizeSource(p.source));
      const cn = [p.carrier, p.number].filter(Boolean).join(' ');
      if (cn) parts.push(cn);
      if (p.direction) parts.push(p.direction);
      return parts.join(' · ') || null;
    }
    case 'shopify_return_created': {
      const parts = [];
      if (p.shopify_return_status) parts.push(`Status ${p.shopify_return_status}`);
      if (p.quantity) parts.push(`Qty ${p.quantity}`);
      if (p.shipping_fee_eur) parts.push(`€${p.shipping_fee_eur} fee`);
      return parts.join(' · ') || null;
    }
    case 'shopify_return_cancelled':
    case 'shopify_return_approved':
    case 'shopify_return_declined':
      return p.new_status ? `→ ${p.new_status}` : null;
    case 'shopify_return_linked': {
      const parts = [];
      if (p.source) parts.push(`via ${p.source}`);
      if (p.reason) parts.push(`Reason ${p.reason}`);
      if (p.promoted_from) parts.push(`promoted from ${p.promoted_from}`);
      return parts.join(' · ') || null;
    }
    case 'shopify_return_unlinked':
      return p.cleared_status ? `cleared ${p.cleared_status}` : null;
    case 'status_change': {
      const parts = [];
      if (p.from && p.to) parts.push(`${p.from} → ${p.to}`);
      else if (p.to) parts.push(`→ ${p.to}`);
      if (p.trigger && p.trigger !== 'manual') parts.push(`Trigger: ${p.trigger}`);
      return parts.join(' · ') || null;
    }
    case 'refund_completed': {
      const parts = [];
      if (p.amount_eur) parts.push(formatMoney(p.amount_eur));
      if (p.gateway) parts.push(p.gateway);
      if (p.trigger) parts.push(p.trigger);
      return parts.join(' · ') || null;
    }
    case 'refund_failed':
      return p.reason || null;
    case 'lsm_event_received': {
      const evt = p.lsm_event_type || p.trigger;
      const parts = [];
      if (evt) parts.push(evt);
      if (p.rt_number) parts.push(`RT ${p.rt_number}`);
      else if (p.dn_number) parts.push(`DN ${p.dn_number}`);
      if (p.from && p.to && p.from !== p.to) parts.push(`${p.from} → ${p.to}`);
      if (p.quality) parts.push(`Grade ${p.quality}`);
      return parts.join(' · ') || null;
    }
    case 'lsm_milestone_booked':
      return p.rt_number ? `RT ${p.rt_number} booked` : null;
    case 'case_auto_created': {
      const parts = [];
      if (p.trigger) parts.push(p.trigger);
      if (p.case_type) parts.push(`Type ${p.case_type}`);
      // N2b. Der Rohwert bleibt in der Nutzlast; nur die ANZEIGE folgt der Karte.
      // Gemessen: 38 Ereigniszeilen tragen `issue_type`, 6 Werte, davon **0
      // unbekannt** — im Archiv steht heute kein Zombie, und ein spaeterer
      // bliebe unter dieser Regel ohnehin roh stehen.
      if (p.issue_type) parts.push(`Issue ${issueTypeAnzeige(p.issue_type, { fallback: null })}`);
      if (typeof p.llm_confidence === 'number') parts.push(`Confidence ${Math.round(p.llm_confidence * 100)}%`);
      if (p.has_additional_topics) parts.push('+ topics');
      return parts.join(' · ') || null;
    }
    case 'additional_topic_detected': {
      const parts = [];
      if (p.issue_type) parts.push(issueTypeAnzeige(p.issue_type, { fallback: null }));   // N2b, wie oben
      if (p.product_hint) parts.push(p.product_hint);
      if (typeof p.confidence === 'number') parts.push(`${Math.round(p.confidence * 100)}%`);
      return parts.join(' · ') || null;
    }
    case 'topic_split_into_case':
      return p.child_case_number
        ? `→ ${p.child_case_number}`
        : (p.parent_case_number ? `from ${p.parent_case_number}` : null);
    case 'comm_outbound':
      return [_fmt(p.kind), _fmt(p.recipient)].filter(Boolean).join(' · ') || null;
    case 'comm_inbound':
      return _fmt(p.sender) || null;
    case 'sla_approaching':
    case 'sla_breached': {
      // Keys = the backend SLA `type` (rma_sla.py → payload.sla_kind). Labels live
      // in the module-level SLA_KIND_LABELS (shared with the SLA Overview page so
      // labels never drift); unknown keys fall back to a humanized underscore-strip.
      const parts = [];
      const kindLabel = SLA_KIND_LABELS[p.sla_kind] || (p.sla_kind || '').replace(/_/g, ' ');
      if (kindLabel) parts.push(kindLabel);
      // Track A5-4b. Three things about this site, none of them obvious:
      //
      // 1. `deadline_date` is NOT a date column despite the name — the backend
      //    fills it from `sla["due_at"]` (rma_sla.py:587, :634), a full
      //    INSTANT. So: no `dateOnly`. Passing `dateOnly` here would pin the
      //    day the string names in UTC and read a day early for any deadline in
      //    the 22:00-24:00 UTC band.
      // 2. It stays ABSOLUTE, deliberately. This string lands inside an
      //    `sla_approaching` / `sla_breached` AUDIT entry — a permanent record
      //    of what the deadline was when the system acted on it. Someone
      //    reading that trail a week later needs the deadline, not its distance
      //    from the moment they happened to open the page; "vor 3 Tagen" would
      //    re-measure a fixed fact against a moving now on every render.
      // 3. It is a plain STRING (the timeline model's `detail` field), so this
      //    is `formatBerlinDateTime`, not <CaseTime> — same central parser.
      //
      // Format delta: `dateStyle:'short'` rendered a TWO-digit year
      // ("19.04.26"); the shared formatter renders four ("19.04.2026, 12:00").
      // Same propagation A5-4a already shipped for the timeline row itself.
      if (p.deadline_date) {
        const d = parseTimestamp(p.deadline_date);
        if (d) {
          const stamp = formatBerlinDateTime(d);
          parts.push(d.getTime() < Date.now() ? `Deadline was ${stamp}` : `Deadline ${stamp}`);
        }
      }
      return parts.join(' · ') || null;
    }
    case 'customer_response_logged':
    case 'vendor_response_logged':
      return _fmt(p.intent) || _fmt(p.from_email) || null;
    case 'order_reassigned': {
      // MUST return a non-empty string on every path. The payload carries BOTH
      // customer addresses (`case_email` / `order_email`); a null summary flips
      // `showPayload` on in toActivityEntry() and the whole payload renders as
      // raw JSON in the case feed's default All tab (and ActivityEntryRow
      // ignores `expandable` for mono bodies, so it would not even collapse).
      const from = p.from || {};
      const to = p.to || {};
      // Id fallback: LEGACY payloads (pre-migration-184) carry a full GID — see
      // the probe named "legacy payload (pre-184)" in the test file.
      const fromName = _fmt(from.shopify_order_name) || _fmt(shopifyNumericId(from.shopify_order_id));
      const toName = _fmt(to.shopify_order_name) || _fmt(shopifyNumericId(to.shopify_order_id));
      const parts = [];
      // Attach path: `from.shopify_order_id` is null — there is no left side.
      if (from.shopify_order_id && fromName && toName) parts.push(`${fromName} → ${toName}`);
      else if (toName) parts.push(toName);
      // Surface the money-risk signal WITHOUT reprinting either address.
      if (p.email_match === false) parts.push('Customer address differs');
      return parts.join(' · ') || 'Order updated';
    }
    // N106 (2026-10-06): same contract as `order_reassigned` above — MUST return
    // a non-empty string on every path, or the whole payload renders as raw JSON
    // in the default All tab. Names the old and the new position (SKU, falling
    // back to the product title and then to the line item id) plus the mode.
    case 'position_changed': {
      const from = p.from || {};
      const to = p.to || {};
      // `shopify_line_item_id` is a GID by convention (rma_routes.py:6642-6643);
      // `positionLabel` shows its number under the row's own word, "Position".
      const nameOf = (side) => _fmt(side.sku) || _fmt(side.product_title)
        || (side.shopify_line_item_id
          ? positionLabel(side.shopify_line_item_id)
          : null);
      const fromName = nameOf(from);
      const toName = nameOf(to);
      const parts = [];
      if (fromName && toName) parts.push(`${fromName} → ${toName}`);
      else if (toName) parts.push(toName);
      if (Number(p.mode) === 2) parts.push('follows the return');
      // german-words: ok: englischer Satz; "Position" ist der RMA-Fachbegriff (Route /return-positions)
      return parts.join(' · ') || 'Position updated';
    }
    case 'photo_attachment_received':
      return p.attachment_count
        ? `${p.attachment_count} photo${p.attachment_count > 1 ? 's' : ''}`
        : null;
    // E10 Stufe 1: from, to and by whom. Always a non-empty string, so the raw
    // payload never renders in its place.
    case 'resolution_change': {
      const field = RESOLUTION_FIELD_LABELS[p.field] || _fmt(p.field) || 'Resolution';
      return `${_changed(field, _resolutionValue(p.field, p.from), _resolutionValue(p.field, p.to))}`
        + ` · ${originatorText(p.actor)}`;
    }
    case 'return_cost_change': {
      // A part only when its RENDERED value moved. `system_default` and null
      // both read "not set" (see `_deciderValue`), so a move between the two
      // shows no decider part; the originator is always there.
      const parts = [
        ['Voluntary flag', _voluntaryFlagValue(p.from), _voluntaryFlagValue(p.to)],
        ['Reason', _reasonValue(p.reason_from), _reasonValue(p.reason_to)],
        ['Decision', _deciderValue(p.decided_by_from), _deciderValue(p.decided_by_to)],
      ].filter(([, from, to]) => from !== to)
        .map(([field, from, to]) => _changed(field, from, to));
      parts.push(originatorText(p.actor));
      return parts.join(' · ');
    }
    case 'resolution_waived': {
      const parts = [];
      if (_fmt(p.reason)) parts.push(`Reason: ${_quoted(p.reason)}`);
      parts.push(originatorText(p.actor));
      return parts.join(' · ');
    }
    // N59 (2026-09-27): die zwei Spur-Ereignisse. Der Urheber steht NICHT in
    // der Zusammenfassung: die Chronik zeigt Mitarbeiterkonten nirgends im
    // Klartext, und genau das war der Befund an der k:v-Rückfallzeile (Grill W5).
    case 'case_type_changed': {
      // N47 (2026-10-01): die Anlagespur. `from` ist leer, weil es keinen
      // Vorgänger gibt; `unknown → Return` liest sich wie ein Defekt statt wie
      // eine Geburt.
      //
      // R16 W2 (2026-10-02): der Auslöser steht hier NICHT in der Klammer — und
      // der Grund ist nicht, dass er nichts sagt. Er ist das EINZIGE, was eine
      // Autoanlage von einer Handanlage unterscheidet; zwei Anlagen, die sich
      // nur im Auslöser unterscheiden, ergeben hier dieselbe Zeile. Der Grund
      // ist die Darstellung: für die Nicht-LSM-Kanäle lautet er `<kanal>_anlage`
      // (bzw. `anlage`), und dafür gibt es keine Beschriftung — der Rückfall
      // von `labelCaseTypeTrigger()` schriebe „unrecognized trigger: manual
      // anlage", also „unbekannt" über einen wohlbekannten Kanal. Beim
      // LSM-Kanal steht der Kanal ohnehin in den zwei Nachbarzeilen („Case
      // auto-created from LSM return notification", „LSM: …").
      //
      // R16 W3 (2026-10-02): die Zeile bleibt stehen, obwohl sie für LSM-Fälle
      // die dritte über denselben Vorgang ist. Sie ist die EINZIGE Anlagespur
      // für Fälle, die nicht über LSM entstehen (Handanlage, E-Mail, Widerruf);
      // wer sie wegen der LSM-Doppelung streicht, nimmt den übrigen Kanälen
      // ihre Spur.
      if (p.bei_anlage) return `Case created as ${labelCaseType(p.to)}`;
      const trigger = labelCaseTypeTrigger(p.trigger);
      return `${labelCaseType(p.from)} → ${labelCaseType(p.to)}${trigger ? ` (${trigger})` : ''}`;
    }
    case 'case_merged_in': {
      // R16 Blocker 1: Fallart und Status als Etikett, nicht als Rohwert —
      // dieselben Beschriftungen wie überall in der Chronik (`STATUS_LABELS`
      // fällt für unbekannte Schlüssel auf `humanizeStatus` zurück).
      const status = p.source_status ? STATUS_LABELS[p.source_status] : 'unknown status';
      return `${p.source_case_number || 'a case'} merged into this case (was ${labelCaseType(p.source_case_type)}, `
        + `${status}): ${countNoun(p.moved_events, 'event', 'events')}, `
        + `${countNoun(p.moved_comms, 'message', 'messages')}, `
        + `${countNoun(p.moved_inbound, 'inbox mail', 'inbox mails')}`;
    }
    default: {
      // Generic compact summary for unknown event types.
      const compact = Object.entries(p)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join(' · ');
      return compact || null;
    }
  }
}

// ── Payload cleanup ───────────────────────────────────────────────────────────

function _cleanPayload(p) {
  const out = {};
  for (const [k, v] of Object.entries(p || {})) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
}

// ── Transition helper — pre-resolve STATUS_CONFIG colors ─────────────────────

function _statusSide(statusKey) {
  return {
    label: STATUS_LABELS[statusKey] || String(statusKey).replace(/_/g, ' '),
    color: STATUS_CONFIG[statusKey]?.color,
  };
}

// ── Quoted/forwarded thread collapse ──────────────────────────────────────────

/**
 * Split an email body into a reply + collapsed quoted thread.
 *
 * When `body_new` (reply-only, from the backend/inbox `_split_email_thread`)
 * exists and differs from the full body, render the reply as the body and tuck
 * the full thread behind a "Show quoted message" toggle. Otherwise the full body
 * renders as before (non-breaking fallback — guard matches RMAInbox.jsx:1286).
 *
 * When `body_new` is absent — pre-080 rows, comm bodies the backend never
 * split, clients its heuristics did not recognise — the FRONTEND splitter
 * (`splitQuotedThread.js`) is tried as a fallback (§C). `body_new` always
 * wins where it exists; the fallback only ever moves text that was already
 * on screen behind the toggle that was already there, so nothing is lost.
 *
 * @returns {{ body: object|undefined, quotedBody: object|undefined }}
 */
function _bodyWithQuote(fullText, newText, { isQuote = false, expandable = true } = {}) {
  const full = typeof fullText === 'string' ? fullText : '';
  if (!full) return { body: undefined, quotedBody: undefined };
  const nw = typeof newText === 'string' ? newText : '';

  let reply = nw && nw !== full ? nw : null;
  if (!reply) {
    const fallback = splitQuotedText(full);
    if (fallback.quoted) reply = fallback.reply;
  }

  if (reply) {
    return {
      body: { text: reply, expandable, isQuote },
      quotedBody: {
        label: 'Show quoted message',
        openLabel: 'Hide quoted message',
        text: full,
      },
    };
  }
  return { body: { text: full, expandable, isQuote }, quotedBody: undefined };
}

// ── Conversation-only enrichment (ctx.conversation) ───────────────────────────

/** Media types an attachment may be previewed as. Mirrors the `/files/`
 *  inline allowlist in `backend/files_routes.py` — the backend forces a
 *  download for everything else, so a thumbnail for anything else would be a
 *  broken image by construction. */
const THUMBNAIL_MIME_ALLOWLIST = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
]);

/**
 * Human reason for an attachment that has no click target. Three distinct
 * situations, and conflating them misinforms (R16): the file was never kept,
 * the row never had a file, or the file EXISTS and we simply refuse to link
 * somewhere that is not our own origin.
 */
const ATTACHMENT_ERROR_REASON = {
  too_large: 'Too large to store — the file was not kept.',
  save_failed: 'Could not be stored — the file was not kept.',
};
const ATTACHMENT_NO_FILE_REASON = 'Not stored — metadata only.';
const ATTACHMENT_OFF_ORIGIN_REASON =
  'Stored, but the link points off our own file storage — not opened from here.';

/**
 * Root-relative `/files/` URL for one attachment, or null when it has none.
 *
 * `path` is preferred over `url` for the reason `MediaPreview.fileUrl`
 * documents: in prod `url` is absolute on `FILES_PUBLIC_BASE_URL` (the
 * internal onrender host), which is CROSS-ORIGIN to the app. A root-relative
 * path always resolves to our own origin.
 *
 * Anything that does not land on our own `/files/` base gets no href at all
 * — that is what makes "this UI never loads a remote URL" a testable claim
 * rather than a promise, and `attachments_json` is attacker-shaped data.
 */
export function _attachmentHref(att) {
  if (typeof att?.path === 'string' && att.path) {
    return `/files/${att.path.split('/').map(encodeURIComponent).join('/')}`;
  }
  const raw = typeof att?.url === 'string' ? att.url.trim() : '';
  if (!raw) return null;
  if (raw.startsWith('/files/')) return raw;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin === window.location.origin && u.pathname.startsWith('/files/')) {
      return `${u.pathname}${u.search}`;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Normalize `rma_inbound_emails.attachments_json` into render descriptors.
 *
 * Shape written by `_extract_attachments` (backend/rma_routes.py): every
 * entry always carries name/mime_type/size; `url`+`path` are null and an
 * `error` of `too_large` / `save_failed` is set when the disk write was
 * refused, and legacy rows predating disk storage carry metadata only. All
 * three of those render as a chip WITHOUT a click target — a dead link that
 * 404s is worse than a chip that says why it is dead.
 *
 * `rma_communications.attachments` is deliberately NOT read: repo-wide it has
 * NO writer (only a serializer), so wiring it would be a dead branch. It
 * becomes a follow-up the day a writer exists.
 */
function _unavailableReason(att) {
  if (att.error && ATTACHMENT_ERROR_REASON[att.error]) return ATTACHMENT_ERROR_REASON[att.error];
  // It HAS a location, just not one we will link to — say that, rather than
  // claiming nothing was stored.
  if (att.url || att.path) return ATTACHMENT_OFF_ORIGIN_REASON;
  return ATTACHMENT_NO_FILE_REASON;
}

function _attachments(list) {
  if (!Array.isArray(list) || list.length === 0) return undefined;
  const out = [];
  for (const att of list) {
    if (!att || typeof att !== 'object') continue;
    const href = _attachmentHref(att);
    const mime = typeof att.mime_type === 'string' ? att.mime_type.toLowerCase() : '';
    const size = Number(att.size ?? att.size_bytes);
    out.push({
      id: att.id ?? null,
      name: (typeof att.name === 'string' && att.name) ? att.name : 'attachment',
      mimeType: mime || null,
      sizeBytes: Number.isFinite(size) && size > 0 ? size : null,
      href,
      // A thumbnail needs BOTH a same-origin href and a raster type the
      // backend will actually serve inline.
      isImage: !!href && THUMBNAIL_MIME_ALLOWLIST.has(mime),
      unavailableReason: href ? null : _unavailableReason(att),
      // Ein Anhang, der an den Fall verschoben wurde, steht als STEMPEL in
      // `attachments_json` (`stub_fuer_verschobenen_anhang`): die Bytes liegen
      // am Fall, `href` zeigt auf die Fallkopie. Ohne diese zwei Felder saehe er
      // im Fall-Strom wie ein frischer Eingang aus (Befund B2 des R15-Gates zu
      // PR #3150).
      movedToCase: !!att.moved_to_case,
      caseNumber: (typeof att.case_number === 'string' && att.case_number) ? att.case_number : null,
    });
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Sanitized HTML render for one inbound row, or null → the text path.
 *
 * The gate (plan §6), all three conditions required:
 *   - the conversation opt-in is on (never Audit, never legacy flag-OFF),
 *   - the row actually has a `body_html` (pre-080 rows are NULL and fall
 *     through to `_sanitize_body_text`'s plain text, as they always did),
 *   - the inbound was NOT fanned out via `split_segments`. On a fan-out the
 *     raw HTML is the WHOLE thread while each comm is one segment of it, so
 *     rendering it would silently undo the backend splitter and repeat the
 *     entire conversation inside every bubble.
 *
 * FOURTH CONDITION (R15 B5, 2026-08-04): the HTML path may never show MORE
 * thread than the text path already did. If the text side found a quote —
 * `body_new` from the backend splitter, or our own fallback — and the HTML
 * side could not find one, the honest move is to hand the entry back to the
 * text path. Otherwise a bubble that used to show three lines of reply starts
 * showing the whole quote wall the moment `body_html` exists, which is plan
 * goal 3 inverted. Losing the sender's formatting is the cheaper failure:
 * the quote is still one click away either way, and nothing is hidden.
 */
function _emailHtml(source, conversation, { textHasQuote = false } = {}) {
  if (!conversation || !source) return null;
  const raw = source.body_html;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  if (Array.isArray(source.split_segments) && source.split_segments.length > 0) return null;
  const { bodyHtml, quotedHtml } = splitSanitizedEmailHtml(raw);
  if (!bodyHtml) return null;
  // Refused BY US, not absent. The caller turns this into a one-line notice —
  // see BODY_NOTICE_QUOTE_UNSPLIT.
  if (textHasQuote && !quotedHtml) return { refused: 'quote_unsplit' };
  return { bodyHtml, quotedBodyHtml: quotedHtml || undefined };
}

/**
 * Shown when, and ONLY when, we had a formatted version and declined to use
 * it (the §6 fourth condition).
 *
 * [R16-delta warning] Without it, "this mail arrived as plain text" and "we
 * refused this mail's HTML" look identical on screen. The same sender then
 * renders formatted on Monday and raw on Tuesday depending on how her client
 * marked the quote, and the operator reads that as our app being broken. This
 * PR's own spec header says a check that could not run must never be
 * indistinguishable from one that passed; the same courtesy is owed to the
 * person reading the case. Deliberately NOT shown when `body_html` was simply
 * absent — there is nothing to explain then.
 */
const BODY_NOTICE_QUOTE_UNSPLIT =
  'Shown as text — the formatted version could not be split from the quote safely.';

/** Placeholder body for a message whose only content is its attachments. */
const NO_TEXT_BODY = { text: '(no text)', expandable: false, isQuote: true };

/**
 * The real customer of a forwarded inbound email (is_forwarded +
 * original_sender_*). Used as the entry's headline sender so the timeline shows
 * the customer, not the forwarding address (info@brewout.de). Returns null when
 * the entry isn't forwarded or the original sender couldn't be extracted — the
 * caller then falls back to from_name/from_email.
 */
function _forwardedSender(e) {
  if (!e || !e.is_forwarded) return null;
  return e.original_sender_name || e.original_sender_email || null;
}

/**
 * Compact "forwarded via {forwarder}" indicator for a forwarded inbound
 * whose original sender is already shown as the headline. Keeps the info@
 * forwarding address visible without repeating the original sender.
 */
function _forwardedViaLine(e) {
  const via = e && (e.from_name || e.from_email);
  return via ? `forwarded via ${via}` : 'forwarded';
}

// ── Note regex matchers ───────────────────────────────────────────────────────

// Existing matchers (ported from CaseTimeline.jsx)

const TRACKING_REMOVED_RE = /^Tracking entry removed:\s*\*\*([^*]+)\*\*\s*\(carrier:\s*([^,]+),\s*source:\s*([^)]+)\)\.\s*(.*)$/i;
const LSM_RT_RE = /^LSM RT auto-attached:\s*(\S+)\s+\(confidence=([\d.]+),\s*signals=([^)]+)\)/i;
const SHOPIFY_RETURN_CREATED_RE = /^Shopify return created:\s*\*\*(gid:\/\/shopify\/Return\/(\d+))\*\*\s*\(status:\s*([^)]+)\)(?:\s*·\s*Shipping fee:\s*([€$]?[\d.,]+))?/i;

// Backend (_link_existing_return, rma_routes.py):
//   Shopify return {gid} linked (source: **{source}**).[ Return reason: {r}.]
//   [ Case type promoted **{from} → return** (Shopify return is canonical evidence).]
// Curated for the same reason as SHOPIFY_RETURN_CREATED_RE: the raw body is
// English and carries a bare gid:// in a German-first surface. Optional groups
// so a link without a reason or without a promotion still parses.
const SHOPIFY_RETURN_LINKED_RE = /^Shopify return\s+(gid:\/\/shopify\/Return\/(\d+))\s+linked\s*\(source:\s*\*\*(\w+)\*\*\)\.?(?:\s*Return reason:\s*([^.]+)\.)?(?:\s*Case type promoted\s*\*\*([a-z_()]+)\s*→\s*return\*\*)?/i;

// New matchers (ground-truthed from grill report + rma_routes.py)
// Backend emits: Refund status `{old}` → `{new}` (trigger: {t}).
const REFUND_STATUS_RE = /^Refund status\s+`?([a-z_]+)`?\s*→\s*`?([a-z_]+)`?\s*\(trigger:\s*([a-z_]+)\)/i;
// Backend emits: Shipment added — {leg_label}: {part1} · {part2} ...
const SHIPMENT_ADDED_RE = /^Shipment added\s+—\s+(.+?):\s+([\s\S]+)$/;

// ── Note-body normalizer ──────────────────────────────────────────────────────

/**
 * Parse a note body into model fields { title, chips, detail, meta, body }.
 * Returns null if entry is not a note.
 */
function _parseNote(body) {
  if (typeof body !== 'string') return null;

  // 1. Tracking removed
  const trackM = body.match(TRACKING_REMOVED_RE);
  if (trackM) {
    const number  = trackM[1].trim();
    const carrier = trackM[2].trim();
    const source  = trackM[3].trim();
    const effect  = trackM[4].trim();
    const chips = [
      chip(number, { mono: true, variant: 'outlined' }),
    ];
    if (carrier && carrier.toLowerCase() !== 'n/a') {
      chips.push(chip(carrier, { variant: 'outlined' }));
    }
    if (source) {
      chips.push(chip(humanizeSource(source), { variant: 'outlined' }));
    }
    return {
      title: 'Tracking removed',
      chips,
      meta: effect || undefined,
    };
  }

  // 2. LSM RT auto-attached
  const lsmM = body.match(LSM_RT_RE);
  if (lsmM) {
    const rtNumber   = lsmM[1].trim();
    const confidence = parseFloat(lsmM[2]);
    const signals    = lsmM[3].split(',').map(s => s.trim()).filter(Boolean);
    const confColor  = confidence >= 0.9 ? 'success' : confidence >= 0.6 ? 'warning' : 'default';
    const chips = [
      chip(rtNumber, { mono: true, variant: 'outlined' }),
      chip(`Confidence ${Math.round(confidence * 100)}%`, { color: confColor, variant: 'outlined' }),
    ];
    if (signals.length > 0) {
      chips.push(chip(`${signals.length} signals`, { variant: 'outlined', tooltip: signals.join(', ') }));
    }
    return {
      title: 'LSM return matched',
      chips,
    };
  }

  // 3. Shopify return created
  const shopM = body.match(SHOPIFY_RETURN_CREATED_RE);
  if (shopM) {
    const numericId = shopM[2];
    const status    = shopM[3].trim();
    const fee       = shopM[4] || null;
    // 🔴 LINK ENTFERNT 2026-09-10. Hier stand
    // `${SHOPIFY_ADMIN}/returns/${numericId}` — eine FLACHE Retouren-Seite, die
    // es in Shopify nicht gibt: eine Retoure liegt UNTER ihrer Bestellung
    // (`…/orders/<order_id>/returns/<return_id>/process`). Der Owner ist am
    // 2026-09-10 im Refund-Dialog in denselben toten Link gelaufen; dort ist er
    // repariert, weil die Bestell-ID vorliegt.
    //
    // HIER liegt sie NICHT: der Eintrag wird aus seinem Text geparst, und
    // `entry.payload` traegt `shopify_order_id` nur bei manchen Ereignisarten.
    // Ein Chip ohne Link ist ehrlich; einer, der 404t, ist schlechter als
    // keiner.
    //
    // *Was das zurueckdreht:* wer `shopify_order_id` des Falls durch
    // `CaseTimeline` nach `toActivityEntry` faedelt, kann den Link in der
    // verschachtelten Form wiederherstellen. Das beruehrt zwei Ebenen und
    // gehoert nicht in einen Fix, der eine Sperre auf dem Geldpfad oeffnet.
    const chips = [
      chip(`#${numericId}`, { mono: true, variant: 'outlined' }),
    ];
    if (status) chips.push(chip(status, { variant: 'outlined' }));
    if (fee) {
      const feeLabel = fee.startsWith('€') ? `${fee} shipping` : `€${fee} shipping`;
      chips.push(chip(feeLabel, { variant: 'outlined' }));
    }
    return {
      title: 'Shopify return created',
      chips,
    };
  }

  // 3b. Shopify return LINKED to an existing case (webhook / discover /
  // create_recovery). Mirrors matcher 3 so the two present identically —
  // before 2026-07-29 this row was hidden from the Relevant tab entirely, so
  // the raw English body was never staff-visible and never got curated.
  const linkM = body.match(SHOPIFY_RETURN_LINKED_RE);
  if (linkM) {
    const numericId    = linkM[2];
    const source       = linkM[3];
    const reason       = (linkM[4] || '').trim();
    const promotedFrom = (linkM[5] || '').trim();
    const chips = [
      // Link entfernt, siehe Begruendung bei Treffer 3 oben.
      chip(`#${numericId}`, {
        mono: true,
        variant: 'outlined',
      }),
    ];
    if (source) chips.push(chip(humanizeSource(source), { variant: 'outlined' }));
    if (reason) chips.push(chip(`Reason ${reason}`, { variant: 'outlined' }));
    return {
      title: 'Shopify return linked',
      chips,
      // The service→return promotion is the consequential part for staff (it
      // moves the case onto the return rail), but it is secondary to "a return
      // was linked" — so it rides as detail, not as a chip.
      ...(promotedFrom
        ? { detail: `Case type promoted: ${promotedFrom} → return` }
        : {}),
    };
  }

  // 4. Refund status transition (NEW — ground-truthed)
  // Backend: Refund status `{old}` → `{new}` (trigger: {t}).
  const refundM = body.match(REFUND_STATUS_RE);
  if (refundM) {
    const fromStatus = refundM[1].trim();
    const toStatus   = refundM[2].trim();
    const trigger    = refundM[3].trim();
    return {
      title: 'Refund-Status',
      chips: [transition(_statusSide(fromStatus), _statusSide(toStatus))],
      meta: `trigger: ${trigger}`,
    };
  }

  // 5. Shipment added (NEW — ground-truthed)
  // Backend: Shipment added — {leg_label}: {part1 · part2 ...}\n{optional note}
  const shipM = body.match(SHIPMENT_ADDED_RE);
  if (shipM) {
    const legLabel   = shipM[1].trim();
    const rest       = shipM[2];
    // Split off any trailing note lines (\n...) from the chip-parts line
    const [firstLine, ...noteLines] = rest.split('\n');
    const parts = firstLine.split(' · ').map(s => s.trim()).filter(Boolean);
    const metaParts = noteLines.map(l => l.trim()).filter(Boolean);

    const chips = [];
    // Regex to detect a trailing tracking-like token (6+ digit sequence) after
    // a space — matches backend format "Hermes 1234567890123" (space-joined).
    const TRAILING_TRACKING_RE = /\s(\S*\d{6,}\S*)$/;
    for (const part of parts) {
      if (/^\d{6,}/.test(part)) {
        // Tracking number (6+ digit prefix) — standalone after ` · ` split
        chips.push(chip(part, { mono: true, variant: 'outlined' }));
      } else if (/^DN\s+/i.test(part) || /^RT\s+/i.test(part)) {
        // Document/Return number
        chips.push(chip(part, { mono: true, variant: 'outlined' }));
      } else {
        // Check for space-joined "carrier tracking" format (e.g. "Hermes 1234567890123")
        const trailingM = part.match(TRAILING_TRACKING_RE);
        if (trailingM) {
          const tracking = trailingM[1];
          const carrier = part.slice(0, part.length - trailingM[0].length).trim();
          if (carrier) chips.push(chip(carrier, { variant: 'outlined' }));
          chips.push(chip(tracking, { mono: true, variant: 'outlined' }));
        } else if (part) {
          // Carrier name or other label without embedded tracking
          chips.push(chip(part, { variant: 'outlined' }));
        }
      }
    }

    return {
      title: 'Shipment added',
      detail: legLabel,
      chips: chips.length > 0 ? chips : undefined,
      meta: metaParts.length > 0 ? metaParts.join(' · ') : undefined,
    };
  }

  // 6. Free-text note (no matcher hit)
  return {
    title: FREE_TEXT_NOTE_TITLE,
    body: { text: body, expandable: true },
  };
}

/**
 * True when a `note` comm is HAND-WRITTEN prose — i.e. it hit none of the
 * structural matchers above and is not a Smart-Compose draft basis.
 *
 * Exported so the V2 conversation view can tell staff-authored notes (which
 * belong on the conversation rail as a bubble) from machine-written ones
 * (tracking / LSM / refund-status / Shopify-return, which collapse to an
 * interstitial) WITHOUT re-declaring the matchers — one regex set, one answer.
 *
 * @param {Object} entry  Raw timeline entry.
 * @returns {boolean}
 */
export function isFreeTextNote(entry) {
  if (!entry || entry.kind !== 'comm' || entry.type !== 'note') return false;
  if (typeof entry.body !== 'string' || !entry.body) return false;
  if (entry.body.startsWith(SMART_COMPOSE_PREFIX)) return false;
  const parsed = _parseNote(entry.body);
  return !!parsed && parsed.title === FREE_TEXT_NOTE_TITLE;
}

// ── Actions builder ───────────────────────────────────────────────────────────

/**
 * The per-message action row for comm/inbound entries.
 *
 * SHAPE (§E, 2026-08-04): three affordances used to sit side by side as bare
 * icons on every message, which read as three equally-likely things to do.
 * They are not. **Re-process** is the one an operator reaches for while
 * working a case, so it stays a direct icon. **Remove from case** (a
 * destructive correction) and **Open in inbox** (a navigation away) move
 * into the ⋯ overflow — the same `IconButton` + `Menu` pattern the rest of
 * the case detail already uses.
 *
 * Open-in-inbox stays a `RouterLink`, now as `MenuItem component={RouterLink}`,
 * so cmd-click / middle-click / open-in-new-tab keep working. A `useNavigate`
 * handler would have silently taken that away.
 *
 * WHY THIS IS A MODULE-LEVEL COMPONENT AND MUST STAY ONE (R16 B1, 2026-08-04)
 * ---------------------------------------------------------------------------
 * It used to be declared INSIDE `_buildActions`, so every `toActivityEntry()`
 * call produced a brand-new component TYPE. React compares types by identity:
 * a new type at the same position is a different component, so it unmounts the
 * old tree and mounts a fresh one — throwing away all local state.
 * `ConversationStream` calls `toModel` on every render, so every parent render
 * reset this row. That was latent while the only state was a spinner (a lost
 * spinner looks like a fast request); §E made it fatal, because `menuAnchor`
 * is now the ONLY route to "Remove from this case" and "Open in inbox" — the
 * menu closed itself roughly half a second after opening. Measured: two menu
 * items at 50/200 ms, none at 600/1500/3000 ms.
 *
 * Declaring a component inside a function that runs per render is the bug, not
 * the state. Keep it here, take `inboundId` + the callbacks as props.
 */
export function ActionButtons({ inboundId, onReprocessed, onUnlinked }) {
  const [loading, setLoading] = React.useState(false);
  const [unlinking, setUnlinking] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const busy = loading || unlinking;

  const closeMenu = () => setMenuAnchor(null);

  const handleReprocess = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/rma/inbox/${inboundId}/reprocess`);
      onReprocessed?.();
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async () => {
    closeMenu();
    setUnlinking(true);
    try {
      await axios.post(`${API}/api/rma/inbox/${inboundId}/unlink-case`, { mark_wrong_match: true });
      onUnlinked?.(inboundId);
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Tooltip title="Reprocess email (re-run matching + tracking extraction)" arrow>
        <span>
          <IconButton
            size="small"
            aria-label="Reprocess email"
            onClick={handleReprocess}
            disabled={busy}
            sx={{ p: 0.25 }}
          >
            {loading ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </span>
      </Tooltip>
      {/* A conversation renders n of these, so a bare "More actions" would put
          n identically-named buttons in the a11y tree with nothing to tell them
          apart (R16). The message is named in the label. */}
      <Tooltip title="More actions for this message" arrow>
        <span>
          <IconButton
            size="small"
            aria-label="More actions for this message"
            aria-haspopup="menu"
            aria-expanded={menuAnchor ? true : undefined}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={busy}
            sx={{ p: 0.25 }}
          >
            {unlinking ? <CircularProgress size={12} /> : <MoreHorizIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {/* Board gap D (2026-08-02): jump from the case timeline to the
            original mail — RMAInbox reads ?email_id, pins + expands +
            scrolls to that mail. */}
        {inboundId != null && (
          <MenuItem
            component={RouterLink}
            to={`/rma/inbox?email_id=${inboundId}`}
            onClick={closeMenu}
          >
            <ListItemIcon><InboxOutlinedIcon sx={{ fontSize: 16 }} /></ListItemIcon>
            <ListItemText>Open in inbox</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleUnlink}>
          <ListItemIcon><LinkOffIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText
            primary="Remove from this case"
            secondary="Flagged as a mismatch for training"
          />
        </MenuItem>
      </Menu>
    </Stack>
  );
}

/** Element factory — the model carries a NODE, the component type is stable. */
function _buildActions(inboundId, { onReprocessed, onUnlinked } = {}) {
  return (
    <ActionButtons
      inboundId={inboundId}
      onReprocessed={onReprocessed}
      onUnlinked={onUnlinked}
    />
  );
}

// ── Main normalizer ───────────────────────────────────────────────────────────

/**
 * toActivityEntry(entry, ctx?) → ActivityEntryModel
 *
 * Normalizes a raw RMA timeline entry into an ActivityEntryModel descriptor
 * suitable for ActivityEntryRow.
 *
 * @param {Object} entry  Raw entry from buildRmaEntries() in CaseTimeline
 * @param {Object} [ctx]  { onReprocessed?, onUnlinked? }
 */
export function toActivityEntry(entry, ctx = {}) {
  const id        = entry.id || `entry-${entry.date}`;
  const timestamp = entry.date;
  const conversation = ctx.conversation === true;

  // ── 1. Event entries (kind === 'event') ──────────────────────────────────
  if (entry.kind === 'event') {
    const label   = eventLabel(entry);
    const cleaned = _cleanPayload(entry.payload);
    const summary = eventSummary(entry);
    const hasPayload = Object.keys(cleaned).length > 0;
    const showPayload = hasPayload && (!summary || DEBUG_TYPES.has(entry.type));

    // Actor meta. Regel-16-Tor C2 (E10): the three decision traces name their
    // originator in the detail, so they get no role line. It would add a second
    // "by" that can contradict the first (`return_cost_change` carries the NEW
    // decider's role, e.g. "by system" under "by <account>"), and for a missing
    // originator it would put back the role fallback that E10 §3.6 rules out.
    const meta = ORIGINATOR_IN_DETAIL.has(entry.type)
      ? undefined
      : (entry.actor_change === 'system'
        ? 'by system'
        : (entry.actor_change ? `by ${entry.actor_change}` : undefined));

    // Chips for status_change
    let chips;
    if (entry.type === 'status_change') {
      const p = entry.payload || {};
      if (p.from && p.to) {
        chips = [transition(_statusSide(p.from), _statusSide(p.to))];
      } else if (p.to) {
        chips = [transition({ label: '…', color: 'default' }, _statusSide(p.to))];
      }
    }

    return {
      id,
      timestamp,
      title: label,
      detail: summary || undefined,
      chips,
      meta,
      body: showPayload
        ? { text: JSON.stringify(cleaned, null, 2), expandable: true, mono: true }
        : undefined,
    };
  }

  // ── 2. Comm entries (kind === 'comm') ────────────────────────────────────
  if (entry.kind === 'comm') {
    const type = entry.type;

    // Smart-compose draft-basis notes: the body is prefixed with
    // SMART_COMPOSE_PREFIX and renders as a draft-origin row.
    const isSmartComposeNote = type === 'note' &&
      typeof entry.body === 'string' &&
      entry.body.startsWith(SMART_COMPOSE_PREFIX);

    // Note body parsing (structural matchers)
    if (type === 'note' || type === 'shopify_return_created' || type === 'shopify_return_linked') {
      const noteBody = typeof entry.body === 'string' ? entry.body : null;
      const parsed = noteBody ? _parseNote(noteBody) : null;

      if (parsed && parsed.title !== FREE_TEXT_NOTE_TITLE) {
        // Structured note (tracking_removed / lsm_rt / shopify_return_created /
        // refund-status / shipment-added)
        const chips = [];
        if (entry.mergedCount > 1) {
          const mins = _minutesBetween(entry.mergedLatestDate, entry.date);
          chips.push(chip(`×${entry.mergedCount}`, {
            variant: 'outlined',
            tooltip: `${entry.mergedCount}× within ${mins} min — duplicates merged`,
          }));
        }
        const allChips = [...chips, ...(parsed.chips || [])];

        return {
          id,
          timestamp,
          title: parsed.title,
          chips: allChips.length > 0 ? allChips : undefined,
          detail: parsed.detail,
          meta: parsed.meta,
          body: parsed.body,
        };
      }

      // Smart-compose note (draft basis — shown as quoted text, not a body)
      if (isSmartComposeNote) {
        const noteText = entry.body.slice(SMART_COMPOSE_PREFIX.length);
        return {
          id,
          timestamp,
          title: 'Smart Compose — draft basis',
          body: { text: noteText, isQuote: true },
        };
      }

      // Free-text note or shopify_return_created without a match
      if (parsed) {
        return {
          id,
          timestamp,
          title: parsed.title || FREE_TEXT_NOTE_TITLE,
          chips: parsed.chips,
          detail: parsed.detail,
          meta: parsed.meta,
          body: parsed.body,
        };
      }
    }

    // Regular comm entry (email_sent, vendor_response_logged, etc.)
    const typeLabel = COMM_LABELS[type] || type.replace(/_/g, ' ');
    const subject   = entry.subject || undefined;

    // ── comm status_change → colored transition chips ────────────────────────
    // When a comm row has type === 'status_change', the body encodes the
    // transition as "Status changed from **open** to **resolved**." Parse it
    // and surface chips instead of the raw body text so it matches the
    // CommEntry rendering in master CaseTimeline.jsx.
    if (type === 'status_change') {
      const statusChangeMatch = typeof entry.body === 'string'
        ? entry.body.match(/(?:from|von)\s+\*?\*?([a-z_]+)\*?\*?\s+(?:to|auf|→|->)\s+\*?\*?([a-z_]+)\*?\*?/i)
        : null;
      const fromKey = statusChangeMatch?.[1] || null;
      const toKey   = statusChangeMatch?.[2] || null;
      const scTitle = COMM_LABELS.status_change || 'Status change';
      const scChips = (fromKey && toKey)
        ? [transition(_statusSide(fromKey), _statusSide(toKey))]
        : undefined;
      const titleAdornment = entry.created_by === 'smart_compose'
        ? <AutoAwesomeIcon sx={{ fontSize: 12, color: 'primary.main' }} />
        : undefined;
      return {
        id,
        timestamp,
        title: scTitle,
        titleAdornment,
        chips: scChips,
        // body intentionally omitted — chips render the transition; raw body is redundant
      };
    }

    // ── smart_compose titleAdornment (all comm types except status_change) ──
    const titleAdornment = entry.created_by === 'smart_compose'
      ? <AutoAwesomeIcon sx={{ fontSize: 12, color: 'primary.main' }} />
      : undefined;

    // To:/CC line → meta
    let meta;
    if (entry.to_email) {
      meta = `To: ${entry.to_email}`;
      if (entry.cc_emails?.length > 0) {
        meta += ` · CC: ${entry.cc_emails.join(', ')}`;
      }
    }

    // Body — collapse quoted/forwarded thread when the backend attached body_new
    const { body: bodyModel, quotedBody } = _bodyWithQuote(entry.body, entry.body_new, { isQuote: false });

    // source_note carries two different things, so the label is conditional.
    // N92: a row read back from the mailbox's Sent folder writes
    // "gesendet:<folder>". Labelling that "Drafted from note" would state
    // something false about where the text came from.
    const istGesendet = (entry.source_note || '').startsWith('gesendet:');
    const secondaryBody = entry.source_note
      ? {
          label: istGesendet ? 'Sent from mailbox' : 'Drafted from note',
          text: istGesendet
            ? entry.source_note.slice('gesendet:'.length)
            : entry.source_note,
        }
      : undefined;

    // Merged ×N badge
    const chips = [];
    if (entry.mergedCount > 1) {
      const mins = _minutesBetween(entry.mergedLatestDate, entry.date);
      chips.push(chip(`×${entry.mergedCount}`, {
        variant: 'outlined',
        tooltip: `${entry.mergedCount}× within ${mins} min — duplicates merged`,
      }));
    }

    return {
      id,
      timestamp,
      title: typeLabel,
      titleAdornment,
      chips: chips.length > 0 ? chips : undefined,
      detail: subject,
      meta,
      body: bodyModel,
      quotedBody,
      secondaryBody,
    };
  }

  // ── 3. Inbound entries (kind === 'inbound') ──────────────────────────────
  if (entry.kind === 'inbound') {
    // Forwarded mail: show the real customer (original sender) as the headline,
    // not the info@ forwarding address (case 2136). Falls back to from_* when
    // not forwarded or the original sender couldn't be extracted.
    const fwdSender = _forwardedSender(entry);
    const displayTitle = fwdSender || entry.from_name || entry.from_email || 'Inbound Email';
    const subject = entry.subject || '(no subject)';

    // Master InboundEntry showed sender(title) + subject + llm_summary + rawBody
    // as four distinct fields. Keep the subject on the detail line (never drop it
    // when an llm_summary exists). The llm_summary is NO LONGER shown next to the
    // raw body (§E, 2026-08-04): it restated what the body already said, one
    // collapse-toggle below it. It is a machine paraphrase of text the operator
    // can read directly — the fallback in the merged branch, where no raw body
    // exists, is the one place it still earns its screen space.
    // Collapse the quoted/forwarded thread when body_new (inbox splitter) differs.
    const { body: bodyModel, quotedBody } = _bodyWithQuote(
      entry.body_text, entry.body_new, { isQuote: !conversation },
    );

    const htmlOrRefusal = _emailHtml(entry, conversation, { textHasQuote: !!quotedBody });
    const html = htmlOrRefusal?.refused ? null : htmlOrRefusal;
    const bodyNotice = htmlOrRefusal?.refused ? BODY_NOTICE_QUOTE_UNSPLIT : undefined;
    const attachments = conversation ? _attachments(entry.attachments_json) : undefined;

    const actions = _buildActions(entry.id, ctx);

    return {
      id,
      timestamp,
      title: displayTitle,
      detail: subject,
      // When the original sender is the headline, show a compact "via {info@}"
      // indicator; when it's forwarded but the sender couldn't be extracted,
      // still signal the forward (matches the inbox's always-on marker).
      meta: fwdSender ? _forwardedViaLine(entry) : (entry.is_forwarded ? 'forwarded' : undefined),
      // The text body is kept even when `bodyHtml` exists: the renderer picks
      // ONE of the two, and a consumer that cannot render HTML must degrade to
      // the text rather than to nothing.
      body: bodyModel || (attachments && !html ? NO_TEXT_BODY : undefined),
      quotedBody,
      ...(html || {}),
      bodyNotice,
      attachments,
      actions,
    };
  }

  // ── 4. Merged entries (kind === 'merged') ────────────────────────────────
  if (entry.kind === 'merged') {
    const comm   = entry.comm || {};
    const inbound = entry.inbound || {};

    const type = comm.type || '';
    const typeLabel = COMM_LABELS[type] || type.replace(/_/g, ' ');
    const displaySubject = comm.subject || inbound.subject || '(no subject)';
    // Forwarded mail: headline the real customer, not the info@ forwarder.
    const fwdSender = _forwardedSender(inbound);
    const senderLine = fwdSender || inbound.from_name || inbound.from_email || '';

    // Sender → meta
    let meta = senderLine ? `· ${senderLine}` : undefined;

    // To:/CC from comm
    if (comm.to_email) {
      const toCc = `To: ${comm.to_email}${(comm.cc_emails?.length > 0) ? ` · CC: ${comm.cc_emails.join(', ')}` : ''}`;
      meta = meta ? `${meta} · ${toCc}` : toCc;
    }

    // Forwarded indicator: "via {info@}" when the customer is the headline,
    // else a bare "forwarded" when forwarded but the sender wasn't
    // extracted (keeps the forward signal, matching the inbox).
    const fwdLine = fwdSender ? _forwardedViaLine(inbound) : (inbound.is_forwarded ? 'forwarded' : undefined);
    if (fwdLine) meta = meta ? `${meta} · ${fwdLine}` : fwdLine;

    // Chips: ×N merged badge
    const chips = [];
    if (comm.mergedCount > 1 || entry.mergedCount > 1) {
      const mc = comm.mergedCount || entry.mergedCount || 1;
      const latestDate = comm.mergedLatestDate || entry.mergedLatestDate;
      const baseDate = comm.created_at || inbound.received_at || entry.date;
      const mins = latestDate ? _minutesBetween(latestDate, baseDate) : 0;
      chips.push(chip(`×${mc}`, {
        variant: 'outlined',
        tooltip: `${mc}× within ${mins} min — duplicates merged`,
      }));
    }

    // Body from the comm (the human message).
    //
    // §E (2026-08-04): the llm_summary is no longer shown ALONGSIDE the body.
    // It restated, one toggle lower, text the operator can read directly —
    // a machine paraphrase competing with the source. Removed globally, not
    // per-context: one behaviour, no ctx fork.
    //
    // The FALLBACK below stays, and is the reason the removal costs nothing:
    // where a merged entry has no comm body at all, the summary IS the body,
    // so the information never disappears — only the duplicate display does.
    const commBody = comm.body;
    const hasBody  = typeof commBody === 'string' && commBody;
    const llmSummary = inbound.llm_summary || undefined;

    let bodyModel;
    let quotedBody;
    if (hasBody) {
      // Collapse the quoted/forwarded thread on the comm body (body_new attached
      // at _build_case_response for outbound / logged-response comms).
      ({ body: bodyModel, quotedBody } = _bodyWithQuote(commBody, comm.body_new, { isQuote: false }));
    } else if (llmSummary) {
      bodyModel = { text: llmSummary, expandable: false, isQuote: true };
    }

    const htmlOrRefusal = _emailHtml(inbound, conversation, { textHasQuote: !!quotedBody });
    const html = htmlOrRefusal?.refused ? null : htmlOrRefusal;
    const bodyNotice = htmlOrRefusal?.refused ? BODY_NOTICE_QUOTE_UNSPLIT : undefined;
    const attachments = conversation ? _attachments(inbound.attachments_json) : undefined;

    const actions = _buildActions(inbound.id, ctx);

    return {
      id,
      timestamp,
      title: typeLabel,
      chips: chips.length > 0 ? chips : undefined,
      detail: displaySubject,
      meta,
      body: bodyModel || (attachments && !html ? NO_TEXT_BODY : undefined),
      quotedBody,
      ...(html || {}),
      bodyNotice,
      attachments,
      actions,
    };
  }

  // Fallback — unknown kind
  return {
    id,
    timestamp,
    title: entry.type || entry.kind || 'Unknown',
    body: { text: JSON.stringify(entry, null, 2), expandable: true, mono: true },
  };
}

/**
 * A1 / R16 W1 and recommendation 2 (2026-11-04): the visible label of a line item id, used by the
 * `position_changed` summary. The row headline of the same entry says
 * "Position changed" (EVENT_LABELS, the RMA word for the object — route
 * /return-positions), so the detail uses the same word: one object, one name.
 *
 * The prefix is set ONLY when `shopifyNumericId` really reduced a GID to its
 * number (or the value already is one). Every other value comes back unchanged
 * and is printed as-is — "position gid://shopify/Product/1" would be a label
 * that asserts a position the id does not name.
 *
 * @param {string|number} id
 * @returns {string}
 */
function positionLabel(id) {
  const value = shopifyNumericId(id);
  return typeof value === 'number' || /^\d+$/.test(String(value))
    ? `position ${value}`
    : String(value);
}
